/**
 * symbolLibrary.js
 * ----------------------------------------------------------------------------
 * Símbolos SVG paramétricos dos recortes (cuba, cooktop, furos...), no estilo
 * de prancha técnica (line-art). Cada símbolo é gerado a partir do retângulo
 * em px do recorte e devolve PRIMITIVAS genéricas que o DrawingCanvas (e a
 * futura vista isométrica/impressão) renderizam sem conhecer cada tipo:
 *
 *   { type: 'rect'|'circle'|'line', class: 'std-cutout-outline'|'std-cutout-detail', ...attrs }
 *
 * PONTO DE EXTENSÃO: novo símbolo = nova função aqui + entrada em
 * CUTOUT_TYPES (cutoutUtils.js). Nada muda no canvas.
 */

function rect(x, y, w, h, cls, rx = 0) {
  return { type: "rect", class: cls, x, y, width: w, height: h, rx };
}
function circle(cx, cy, r, cls) {
  return { type: "circle", class: cls, cx, cy, r };
}
function line(x1, y1, x2, y2, cls, dash) {
  return { type: "line", class: cls, x1, y1, x2, y2, dash: dash || null };
}

/** Cuba simples: retângulo arredondado + rebaixo interno + válvula. */
function sinkSymbol(b) {
  const inset = Math.min(b.w, b.h) * 0.12;
  return [
    rect(b.x, b.y, b.w, b.h, "std-cutout-outline", Math.min(b.w, b.h) * 0.12),
    rect(
      b.x + inset,
      b.y + inset,
      b.w - inset * 2,
      b.h - inset * 2,
      "std-cutout-detail",
      Math.min(b.w, b.h) * 0.08
    ),
    circle(b.x + b.w / 2, b.y + b.h / 2, Math.min(b.w, b.h) * 0.08, "std-cutout-detail")
  ];
}

/** Cuba dupla: duas cubas lado a lado com divisória. */
function sinkDoubleSymbol(b) {
  const gap = b.w * 0.04;
  const half = (b.w - gap) / 2;
  const left = { x: b.x, y: b.y, w: half, h: b.h };
  const right = { x: b.x + half + gap, y: b.y, w: half, h: b.h };
  return [
    rect(b.x, b.y, b.w, b.h, "std-cutout-outline", Math.min(b.w, b.h) * 0.1),
    ...sinkSymbol(left).slice(1),
    rect(left.x, left.y, left.w, left.h, "std-cutout-detail", Math.min(half, b.h) * 0.1),
    ...sinkSymbol(right).slice(1),
    rect(right.x, right.y, right.w, right.h, "std-cutout-detail", Math.min(half, b.h) * 0.1)
  ];
}

/** Cooktop: contorno + 4 bocas (círculo duplo). */
function cooktopSymbol(b) {
  const r = Math.min(b.w, b.h) * 0.16;
  const cxs = [b.x + b.w * 0.28, b.x + b.w * 0.72];
  const cys = [b.y + b.h * 0.3, b.y + b.h * 0.7];
  const burners = [];
  cxs.forEach(cx => {
    cys.forEach(cy => {
      burners.push(circle(cx, cy, r, "std-cutout-detail"));
      burners.push(circle(cx, cy, r * 0.45, "std-cutout-detail"));
    });
  });
  return [
    rect(b.x, b.y, b.w, b.h, "std-cutout-outline", Math.min(b.w, b.h) * 0.06),
    ...burners
  ];
}

/** Recorte/furo circular. */
function circleSymbol(b) {
  const r = Math.min(b.w, b.h) / 2;
  return [circle(b.x + b.w / 2, b.y + b.h / 2, r, "std-cutout-outline")];
}

/** Recorte retangular genérico (tracejado, como recorte a fazer). */
function rectSymbol(b) {
  return [
    {
      ...rect(b.x, b.y, b.w, b.h, "std-cutout-outline"),
      dash: "5 3"
    }
  ];
}

const SYMBOLS = {
  sink: sinkSymbol,
  sink_double: sinkDoubleSymbol,
  cooktop: cooktopSymbol,
  hole: circleSymbol,
  tap_hole: circleSymbol,
  circle: circleSymbol,
  rect: rectSymbol
};

/**
 * Gera as primitivas do símbolo de um recorte.
 * @param {string} kind tipo do recorte
 * @param {object} pxBox { cx, cy, w, h } em px (centro + tamanho)
 */
export function getCutoutSymbol(kind, pxBox) {
  const b = {
    x: pxBox.cx - pxBox.w / 2,
    y: pxBox.cy - pxBox.h / 2,
    w: pxBox.w,
    h: pxBox.h
  };
  const factory = SYMBOLS[kind] || rectSymbol;
  return factory(b);
}
